import TableView, { TableSection } from '@/components/TableView'
import useDefaultToast from '@/components/useDefaultToast'
import { useIsAppReviewMode } from '@/lib/hooks/useIsAppReviewMode'
import { getIsSandbox, requestRefund } from '@/lib/revenuecat'
import { isBelowiOS18 } from '@/lib/utils/iosCompat'
import { iosUpgradeGuard, showUpgradeBlockedAlertIfNeeded } from '@/modules/handleClickToPay'
import { CurrentUser } from '@/redux/slices/currentUserSlice'
import { useAppSelector } from '@/redux/store'
import getBillingAccountPlanInfo from '@/redux/utils/getBillingAccountPlanInfo'
import { useRouter } from 'expo-router'
import { useEffect, useState } from 'react'
import { Linking, Platform } from 'react-native'
import LogoutAlert from './LogoutAlert'

type ProfileProps = {
  currentUser: CurrentUser
  balance: number
  isOwnerOrAdmin: boolean
}

export default function Profile({ currentUser, balance, isOwnerOrAdmin }: ProfileProps) {
  const router = useRouter()
  const isOwner = useAppSelector(state => state.currentUser.isOwner)
  const toast = useDefaultToast()
  const isAppReviewMode = useIsAppReviewMode()

  const [logoutAlertOpen, setLogoutAlertOpen] = useState(false)
  const [isSandbox, setIsSandbox] = useState(false)

  useEffect(() => {
    if (Platform.OS === 'ios') {
      getIsSandbox().then(setIsSandbox)
    }
  }, [])

  const handleLogoutPress = () => {
    setLogoutAlertOpen(true)
  }

  const handleSwitchUserPress = () => {
    router.push('/profile/switchUser')
  }

  const handleUserInfoPress = () => {
    router.push('/profile/userInfo')
  }

  const handlePlanQuotaPress = () => {
    router.push('/profile/planQuota')
  }

  const handlePlansPress = () => {
    if (showUpgradeBlockedAlertIfNeeded(currentUser)) return
    router.push('/profile/plans')
  }

  const handleIosPaywallPress = () => {
    if (!iosUpgradeGuard({ currentUser })) return
    router.push('/profile/iosPaywall')
  }

  const handleUsersPress = () => {
    router.push('/profile/users')
  }

  const handleBalancePress = () => {
    if (planInfo.isFreeAccount) {
      toast.show('免费用户无法充值，请先升级套餐')
      return
    }

    if (!isOwnerOrAdmin) {
      toast.show('权限不足，仅管理员可充值')
      return
    }

    const cashierParams = {
      title: '余额充值',
      amount: 50,
      cashierType: 'recharge',
    }
    const paramsBase64 = btoa(encodeURIComponent(JSON.stringify(cashierParams)))

    router.push({
      pathname: '/profile/cashier',
      params: { cashierParams: paramsBase64 },
    })
  }

  const handleHelpCenterPress = () => {
    Linking.openURL('https://help.jinshuju.net')
  }

  const handleTermsPress = () => {
    router.push('/profile/terms')
  }

  const handlePrivacyPress = () => {
    router.push('/profile/privacy')
  }

  const handlePermissionsPress = () => {
    router.push('/profile/permissions')
  }

  const handleCustomerServicePress = () => {
    router.push('/profile/customerService')
  }

  const handleFeedbackPress = () => {
    Linking.openURL(`https://im.jsjform.com/f/iajA3e?x_field_1=${Platform.OS}`)
  }

  const handleDiagnosticsPress = () => {
    router.push('/profile/diagnostics')
  }

  const handleRefundPress = async () => {
    await requestRefund()
  }

  const handleSwitchToDesktopPress = () => {
    const hostname = window.location.hostname
    const parts = hostname.split('.')
    parts.shift()
    const domain = parts.join('.')
    document.cookie = `platform=web; path=/; domain=${domain}; max-age=${30 * 24 * 60 * 60}`
    window.location.href = `${process.env.EXPO_PUBLIC_API_BASE_URL}/home`
  }

  const planInfo = getBillingAccountPlanInfo(currentUser.billingAccount)

  const getAccountStatusText = () => {
    if (planInfo.isActiveTrial) {
      const trialTypeText = planInfo.isOrgPlanActiveTrial ? '企业' : '专业增强版'
      return `${trialTypeText}试用中`
    }

    return currentUser.billingAccount.planPackage?.plan.label
  }

  const sections: TableSection[] = [
    {
      title: ' ',
      rows: [
        {
          title: currentUser.displayName,
          subTitle: currentUser.mobile,
          icon: currentUser.avatarUrl || 'person.circle',
          iconSize: 'lg',
          separatorFullWidth: true,
        },
        {
          title: currentUser.billingAccount.name || currentUser.displayName,
          detailText: isAppReviewMode ? undefined : getAccountStatusText(),
          onPress: handleUserInfoPress,
        },
      ],
    },
    {
      title: '',
      rows: [
        {
          title: '填表记录',
          icon: isBelowiOS18 ? 'doc.text' : 'long.text.page.and.pencil',
          onPress: () => router.push('/participated_forms'),
        },
        { title: '回收站', icon: 'trash', onPress: () => router.push('/form_trash') },
      ],
    },
    ...(!isAppReviewMode
      ? [
        {
          title: '团队',
          rows: [
            ...(Platform.OS !== 'ios'
              ? [
                {
                  title: '余额',
                  icon: 'creditcard',
                  detailText: `¥${balance.toFixed(2)}`,
                  onPress: handleBalancePress,
                },
                { title: '升级套餐', icon: 'giftcard', onPress: handlePlansPress },
              ]
              : []),
            ...(Platform.OS === 'ios' && isOwner
              ? [{ title: '升级套餐', icon: 'cart', onPress: handleIosPaywallPress }]
              : []),
            { title: '套餐余量', icon: 'chart.bar', onPress: handlePlanQuotaPress },
            { title: '团队成员', icon: 'person.2', onPress: handleUsersPress },
            ...(isSandbox
              ? [{ title: '申请退款（沙盒）', icon: 'arrow.uturn.backward.circle', onPress: handleRefundPress }]
              : []),
          ],
        },
      ]
      : []),
    {
      title: '帮助',
      rows: [
        { title: '在线客服', icon: 'text.bubble', onPress: handleCustomerServicePress },
        { title: '帮助中心', icon: 'questionmark.circle', onPress: handleHelpCenterPress },
        { title: '使用条款', icon: isBelowiOS18 ? 'doc.text' : 'dock.rectangle', onPress: handleTermsPress },
        { title: '隐私政策', icon: 'lock', onPress: handlePrivacyPress },
      ],
    },
    {
      title: '反馈',
      rows: [
        { title: '反馈问题', icon: 'exclamationmark.bubble', onPress: handleFeedbackPress },
        ...(Platform.OS !== 'web' ? [{ title: '诊断信息', icon: 'info.circle', onPress: handleDiagnosticsPress }] : []),
        ...(Platform.OS !== 'web' ? [{ title: 'App 权限', icon: 'lock.shield', onPress: handlePermissionsPress }] : []),
        ...(Platform.OS === 'web'
          ? [{ title: '切换到电脑版', icon: 'desktopcomputer', onPress: handleSwitchToDesktopPress }]
          : []),
      ],
    },
    {
      title: '',
      rows: [
        {
          title: '切换团队',
          icon: isBelowiOS18 ? 'switch.2' : 'arrow.trianglehead.2.clockwise.rotate.90',
          onPress: handleSwitchUserPress,
        },
        { title: '退出登录', type: 'action' as const, actionType: 'destructive' as const, onPress: handleLogoutPress },
      ],
    },
  ]

  return (
    <>
      <TableView
        sections={sections}
        contentContainerStyle={Platform.OS === 'web' || Platform.OS === 'android' ? { paddingTop: 0 } : undefined}
      />

      <LogoutAlert open={logoutAlertOpen} setOpen={setLogoutAlertOpen} />
    </>
  )
}
